import { useNavigate } from "@tanstack/react-router";

import useStoreAnswer from "actions/quiz/useStoreAnswer";
import { QuizContext } from "context/QuizContext";
import { UserInfoContext } from "context/UserInfoContext";
import useJoyStickNavigation, {
  joystickEvent,
} from "hooks/UseJoyStickNavigation";
import useNavigationAudio from "hooks/useNavigationAudio";
import React, { useContext, useEffect, useState } from "react";
import Circles from "../Circles/Circles";
import MoreInfo from "../MoreInfo/MoreInfo";
import QuizLayout from "../QuizLayout/QuizLayout";

export default function QuizContent() {
  const [selectedCircle, setSelectedCircle] = useState(3);
  const [infoExpanded, setInfoExpanded] = useState(false);
  const [topSelected, setTopSelected] = useState(false);
  const [backSelected, setBackSelected] = useState(false);
  const [endQuizSelected, setEndQuizSelected] = useState(false);
  const [info, setInfo] = useState<string>("");
  const [eventCounter, setEventCounter] = useState(0);
  const [controlsEnabled, setControlsEnabled] = useState(false);
  const { checkmark, select, menu } = useNavigationAudio();
  const [endQuizModal, setEndQuizModal] = useState(false);

  const { userInfo } = useContext(UserInfoContext);

  const {
    questionIndex,
    setQuestionIndex,
    categoryIndex,
    setCategoryIndex,
    currentQuestion,
    categories,
  } = useContext(QuizContext);

  const navigate = useNavigate();

  const { mutateAsync: storeAnswer } = useStoreAnswer();

  useEffect(() => {
    const timer = setTimeout(() => {
      setControlsEnabled(true);
    }, 4500);
    return () => clearTimeout(timer);
  }, []);

  const goToNextQuestion = () => {
    //if there is a next question in the current category, go to it
    if (
      categories &&
      questionIndex + 1 < categories[categoryIndex].questions.length
    ) {
      setQuestionIndex(questionIndex + 1);
    } else if (categories && categoryIndex + 1 < categories.length) {
      //else if there is a next category, go to the first question of that category
      setCategoryIndex(categoryIndex + 1);
      setQuestionIndex(0);
    } else {
      //else, end the quiz
      navigate({ to: "/pick-subjects" });
    }
  };

  const handleJoyStick = (event: joystickEvent) => {
    if (!controlsEnabled) return;
    setEventCounter((prev) => prev + 1);
    switch (event) {
      case "left":
        select.play();
        if (selectedCircle !== 1) {
          if (topSelected) {
            if (questionIndex !== 0) {
              setBackSelected(true);
              setEndQuizSelected(false);
            }
            if (questionIndex === 0 && categoryIndex === 0) {
              return;
            }
            if (questionIndex === 0 && categoryIndex !== 0) {
              setBackSelected(true);
              setEndQuizSelected(false);
            }
          } else {
            setSelectedCircle((prev) => prev - 1);
          }
        }
        break;
      case "right":
        select.play();
        if (selectedCircle !== 5) {
          if (topSelected) {
            setBackSelected(false);
            setEndQuizSelected(true);
          } else {
            setSelectedCircle((prev) => prev + 1);
          }
        }
        break;
      case "down":
        if (!topSelected) {
          menu.play();
          setInfoExpanded((prev) => !prev);
        } else {
          setTopSelected(false);
          setBackSelected(false);
          setEndQuizSelected(false);
        }
        break;
      case "up":
        menu.play();
        setTopSelected(true);
        if (questionIndex === 0 && categoryIndex === 0) {
          setEndQuizSelected(true);
        } else {
          setBackSelected(true);
        }
        break;
      case "enter":
        if (topSelected) {
          if (backSelected) {
            goToPrevQuestion();
          } else if (endQuizSelected) {
            setEndQuizModal(true);
          }
        } else {
          checkmark.play();
          storeAnswer({
            question_id: currentQuestion?.id,
            answer: selectedCircle,
            user_id: userInfo.id,
          });
          goToNextQuestion();
        }
        setSelectedCircle(3);

        break;
      default:
        break;
    }
  };

  useJoyStickNavigation(handleJoyStick);

  useEffect(() => {
    // prevent user from going to previous question when on first question
    if (backSelected && questionIndex === 0 && categoryIndex === 0) {
      setTopSelected(false);
      setBackSelected(false);
    }
    // Dynamically set the info based on the selectedCircle
    if (currentQuestion) {
      const descriptionKey = `description_${selectedCircle}`;
      setInfo(
        currentQuestion?.[descriptionKey as keyof QuestionEntity] as string,
      );
    }
  }, [questionIndex, selectedCircle, currentQuestion, backSelected]);

  function goToPrevQuestion() {
    if (questionIndex === 0 && categoryIndex !== 0) {
      setCategoryIndex((prev) => prev - 1);
      setQuestionIndex(1);
    } else {
      setQuestionIndex((prev) => prev - 1);
    }
  }

  return (
    <>
      <QuizLayout
        title={`${currentQuestion?.question}`}
        backSelected={backSelected}
        endQuizSelected={endQuizSelected}
        eventCounter={eventCounter}
        questionIndex={currentQuestion ? questionIndex : 0}
        endQuizModal={endQuizModal}
        setEndQuizModal={setEndQuizModal}
      >
        <Circles selectedCircle={selectedCircle} />
        {info === "" ? null : (
          <MoreInfo infoExpanded={infoExpanded} info={info} />
        )}
      </QuizLayout>
    </>
  );
}
